// ImageCrop.ts — geometry + canvas export helpers for in-browser image
// cropping. The cropper component and the upload modal share this math so the
// viewport the user sees is exactly what gets uploaded.

export interface Size {
  width: number;
  height: number;
}

export interface CropRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Smallest zoom at which the image fully covers the viewport. */
export const fitScale = (viewport: Size, natural: Size): number => {
  if (natural.width <= 0 || natural.height <= 0) return 1;
  return Math.max(
    viewport.width / natural.width,
    viewport.height / natural.height,
  );
};

/** Keep the offset within bounds so the image always covers the viewport. */
export const clampOffset = (
  offset: { x: number; y: number },
  viewport: Size,
  rendered: Size,
): { x: number; y: number } => ({
  x: Math.min(0, Math.max(viewport.width - rendered.width, offset.x)),
  y: Math.min(0, Math.max(viewport.height - rendered.height, offset.y)),
});

/** Largest region of the image matching the ratio, centered (sensible
 * fallback when no interactive crop was reported). */
export const centerRegion = (natural: Size, aspectRatio: number): CropRegion => {
  const width = natural.width;
  const height = Math.min(natural.height, width / aspectRatio);
  return {
    x: 0,
    y: Math.max(0, (natural.height - height) / 2),
    width,
    height,
  };
};

/** Translate the on-screen crop box back into natural-image coordinates. */
export const regionFromViewport = (
  viewport: Size,
  offset: { x: number; y: number },
  scale: number,
): CropRegion => {
  if (scale <= 0) return { x: 0, y: 0, width: 0, height: 0 };
  return {
    x: Math.max(0, Math.round(-offset.x / scale)),
    y: Math.max(0, Math.round(-offset.y / scale)),
    width: Math.max(1, Math.round(viewport.width / scale)),
    height: Math.max(1, Math.round(viewport.height / scale)),
  };
};

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = url;
  });

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Canvas export failed")),
      "image/jpeg",
      quality,
    );
  });

export interface CropToFileOptions {
  /** Longest output edge in px; never exceeds the source crop size. */
  outputWidth: number;
  /** width / height of the output frame. */
  aspectRatio: number;
  /** Hard byte limit; JPEG quality/downscale is retried until it fits. */
  maxBytes: number;
  fileName?: string;
}

/**
 * Draw the crop region to a canvas and export it as a JPEG File that fits the
 * byte budget. Quality drops first, then dimensions — so even huge photos end
 * up within the limit.
 */
export const cropImageToFile = async (
  sourceUrl: string,
  crop: CropRegion | null,
  options: CropToFileOptions,
): Promise<File> => {
  const { outputWidth, aspectRatio, maxBytes, fileName = "image.jpg" } =
    options;
  const image = await loadImage(sourceUrl);
  const region =
    crop ??
    centerRegion(
      { width: image.naturalWidth, height: image.naturalHeight },
      aspectRatio,
    );

  let width = Math.max(1, Math.min(outputWidth, Math.round(region.width)));
  let quality = 0.92;

  for (let attempt = 0; attempt < 12; attempt++) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = Math.max(1, Math.round(width / aspectRatio));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas is not supported in this browser");
    ctx.drawImage(
      image,
      region.x,
      region.y,
      region.width,
      region.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    const blob = await canvasToBlob(canvas, quality);
    if (blob.size <= maxBytes) {
      return new File([blob], fileName, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    }
    if (quality > 0.4) {
      quality = Math.max(0.4, quality - 0.15);
    } else {
      width = Math.max(64, Math.round(width * 0.75));
    }
  }
  throw new Error("Image could not be compressed under the size limit");
};
